/**
 * Where you are in the product, and where an old link lands.
 *
 * Separate from App.tsx and free of React on purpose: this is the one part of the
 * navigation with real logic in it, and keeping it in a module that imports no
 * components is what lets it be tested without a DOM.
 */

/**
 * Four places: where you stand, add contacts, decide what goes into the CRM, and set the
 * product up.
 *
 * Dashboard came back (2026-11, ContactPilot) for a different reason than the Overview it
 * replaces was removed: that page restated other pages with totals nobody could act on.
 * The Dashboard answers "what needs me, what went to the CRM, what is connected, what next",
 * every number is a real workspace-scoped count, and each one links to the place to act.
 * Still no Contacts — the directory has no read API behind it, so a tab for it would be a
 * promise. `?tab=contacts` lands on Review, where the people ContactPilot found actually are.
 */
export const TABS = [
  { id: 'home', label: 'Dashboard' },
  { id: 'capture', label: 'Capture' },
  { id: 'review', label: 'Review' },
  { id: 'settings', label: 'Settings' },
] as const;
export type TabId = (typeof TABS)[number]['id'];

/**
 * Exactly four settings sections, each one a real customer decision.
 *
 * The one-sentence product: capture a contact, review what ContactPilot found, choose the
 * CRM tags that belong on it, and send it to the CRM. So: which accounts are connected;
 * what happens when an email is marked; what happens when a contact arrives from an image
 * or a file; and which CRM tags exist to choose from.
 *
 * The principle stays: if a customer has no meaningful decision to make, it is not a
 * customer setting. Retired sections were folded into the one holding their decision, or
 * moved behind the support route.
 */
export const SETTINGS_TABS = [
  { id: 'connections', label: 'Connections', slug: 'connections' },
  { id: 'email-capture', label: 'Email Capture', slug: 'email-capture' },
  { id: 'image-file-capture', label: 'Image & File Capture', slug: 'image-file-capture' },
  { id: 'crm-tags', label: 'CRM Tags', slug: 'crm-tags' },
  /*
   * Where approved contacts go besides Keap: Zapier and webhooks (Make, n8n). A real
   * decision — which systems receive the contacts a person approves.
   */
  { id: 'integrations', label: 'Integrations', slug: 'integrations' },
] as const;

/**
 * Settings that inform rather than decide, shown after the four above.
 *
 * Usage & Limits has nothing to choose — a customer cannot change a limit from here or
 * anywhere — so it is not one of the decision sections. It is listed only when the server
 * offers it (USAGE_DASHBOARD_ENABLED), and an address that names it while it is off lands
 * on Connections like any unknown section.
 */
export const INFO_SETTINGS_TABS = [
  { id: 'usage', label: 'Usage & Limits', slug: 'usage' },
] as const;
export type SettingsTabId =
  | (typeof SETTINGS_TABS)[number]['id'] | (typeof INFO_SETTINGS_TABS)[number]['id'];
const ALL_SETTINGS_TABS = [...SETTINGS_TABS, ...INFO_SETTINGS_TABS];

/**
 * Review's two filters, as they appear in the address.
 *
 * The VIEW is primary — "what is waiting for me" — and source is only a filter over it.
 * Four views, each a fixed set of the server's states (reviewModel.ts says which, and
 * why). Kept here, beside the rest of the address logic and free of React, so the parse
 * and the serialize can be tested together: a filter that writes an address it cannot
 * read back is a bookmark that silently lands somewhere else.
 */
export const REVIEW_VIEWS = ['needs_review', 'sent', 'not_sent', 'all'] as const;
export type ReviewView = (typeof REVIEW_VIEWS)[number];
export const REVIEW_SOURCES = ['all', 'email', 'business_card', 'import', 'web_page'] as const;
export type ReviewSourceFilter = (typeof REVIEW_SOURCES)[number];

export interface ReviewFilters {
  readonly view: ReviewView;
  readonly source: ReviewSourceFilter;
}
export const DEFAULT_REVIEW_FILTERS: ReviewFilters = { view: 'needs_review', source: 'all' };

/**
 * Where an old `?state=` link lands: the view that now contains that state.
 *
 * Review used to filter by the server's seven states one at a time. A bookmark of
 * `?state=ready` meant "what can I approve", which is now part of Needs review; `waiting`
 * and `rejected` are both things a person cannot act on, so Not sent.
 */
export const LEGACY_REVIEW_STATES: Readonly<Record<string, ReviewView>> = {
  needs_review: 'needs_review',
  ready: 'needs_review',
  failed: 'needs_review',
  waiting: 'not_sent',
  rejected: 'not_sent',
  sent: 'sent',
  processing: 'all',
  all: 'all',
};

/**
 * `?view=sent&source=email` → the filters. An old `?state=` is translated; anything
 * unrecognised is the default, never an error.
 */
export function parseReviewFilters(search: string): ReviewFilters {
  const params = new URLSearchParams(search);
  const view = params.get('view') ?? '';
  const state = params.get('state') ?? '';
  const source = params.get('source') ?? '';
  return {
    view: (REVIEW_VIEWS as readonly string[]).includes(view)
      ? view as ReviewView
      : Object.hasOwn(LEGACY_REVIEW_STATES, state) ? LEGACY_REVIEW_STATES[state]! : DEFAULT_REVIEW_FILTERS.view,
    source: (REVIEW_SOURCES as readonly string[]).includes(source)
      ? source as ReviewSourceFilter : DEFAULT_REVIEW_FILTERS.source,
  };
}

/** The address Review is shown at. Both filters are always written, so it reads back exactly. */
export function reviewPathFor(filters: ReviewFilters = DEFAULT_REVIEW_FILTERS): string {
  return `/review?view=${filters.view}&source=${filters.source}`;
}

export interface Location {
  readonly tab: TabId;
  readonly settings: SettingsTabId;
  /** Review's filters. Present only when the location is Review. */
  readonly review?: ReviewFilters;
  /** Open the Privacy & data disclosure on Email capture. Only the old privacy link sets it. */
  readonly privacyOpen?: boolean;
  /**
   * The hidden support route. Never linked from navigation; reached only by typing
   * `/support/diagnostics` or `?tab=support-diagnostics`, and rendered for admins only.
   */
  readonly support?: 'diagnostics';
}

/** The address that means "the contact evaluator", for support staff only. */
export const SUPPORT_DIAGNOSTICS_PATH = '/support/diagnostics';
export const SUPPORT_DIAGNOSTICS_TAB = 'support-diagnostics';

/**
 * Every address the app used to answer to, and where it goes now.
 *
 * The app has always been one page with tab state rather than real routes, so nothing
 * can 404 — but people bookmark `?tab=connections`, and the OAuth callbacks return to a
 * path. All of them keep working, and arrive at the section that now holds what they
 * pointed at.
 */
export const LEGACY_TABS: Readonly<Record<string, Location>> = {
  // Primary tabs, current and retired.
  home: { tab: 'home', settings: 'connections' },
  capture: { tab: 'capture', settings: 'connections' },
  review: { tab: 'review', settings: 'connections' },
  settings: { tab: 'settings', settings: 'connections' },
  dashboard: { tab: 'home', settings: 'connections' },
  overview: { tab: 'home', settings: 'connections' },
  card: { tab: 'capture', settings: 'connections' },
  'business-card': { tab: 'capture', settings: 'connections' },
  contacts: { tab: 'review', settings: 'connections' },
  email: { tab: 'review', settings: 'connections' },
  'email-intelligence': { tab: 'review', settings: 'connections' },
  // Settings sections, current.
  connections: { tab: 'settings', settings: 'connections' },
  'email-capture': { tab: 'settings', settings: 'email-capture' },
  'image-file-capture': { tab: 'settings', settings: 'image-file-capture' },
  'crm-tags': { tab: 'settings', settings: 'crm-tags' },
  // Settings sections, retired — each to where its one real decision now lives.
  'capture-settings': { tab: 'settings', settings: 'email-capture' },
  crm: { tab: 'settings', settings: 'crm-tags' },
  automation: { tab: 'settings', settings: 'email-capture' },
  tags: { tab: 'settings', settings: 'crm-tags' },
  'tag-mapping': { tab: 'settings', settings: 'crm-tags' },
  intake: { tab: 'settings', settings: 'image-file-capture' },
  privacy: { tab: 'settings', settings: 'email-capture', privacyOpen: true },
  workspace: { tab: 'settings', settings: 'connections' },
  /*
   * The diagnostics tab and the old test-contact screen. Customers who bookmarked them
   * land on Settings; support staff use the hidden route instead.
   */
  diagnostics: { tab: 'settings', settings: 'connections' },
  test: { tab: 'settings', settings: 'connections' },
  'test-contact': { tab: 'settings', settings: 'connections' },
};

export const DEFAULT_LOCATION: Location = { tab: 'home', settings: 'connections' };

/**
 * Old settings PATHS, and the section each now opens.
 *
 * Separate from LEGACY_TABS because the two namespaces collide: `?tab=capture` is the
 * Capture page, while `/settings/capture` is the old Email capture section — and a consent
 * flow started there before this change still returns to it.
 */
export const LEGACY_SETTINGS_PATHS: Readonly<Record<string, { settings: SettingsTabId; privacyOpen?: boolean }>> = {
  capture: { settings: 'email-capture' },
  'capture-settings': { settings: 'email-capture' },
  automation: { settings: 'email-capture' },
  privacy: { settings: 'email-capture', privacyOpen: true },
  crm: { settings: 'crm-tags' },
  tags: { settings: 'crm-tags' },
  'tag-mapping': { settings: 'crm-tags' },
  intake: { settings: 'image-file-capture' },
  workspace: { settings: 'connections' },
  diagnostics: { settings: 'connections' },
};

/** `/settings/crm-tags` → crm-tags. Old `/connections` return paths count as Connections. */
function sectionFromPath(pathname: string): { settings: SettingsTabId; privacyOpen?: boolean } | null {
  const p = pathname.replace(/\/+$/, '').toLowerCase();
  if (p === '/settings' || p === '/connections') return { settings: 'connections' };
  const m = /^\/settings\/([a-z-]+)$/.exec(p);
  if (!m) return null;
  const current = ALL_SETTINGS_TABS.find((t) => t.slug === m[1])?.id;
  return current ? { settings: current } : LEGACY_SETTINGS_PATHS[m[1]!] ?? null;
}

export function initialLocation(search: string, pathname = '/'): Location {
  const params = new URLSearchParams(search);
  const tabParam = params.get('tab') ?? '';

  if (pathname.replace(/\/+$/, '').toLowerCase() === SUPPORT_DIAGNOSTICS_PATH
    || tabParam === SUPPORT_DIAGNOSTICS_TAB) {
    return { tab: 'settings', settings: 'connections', support: 'diagnostics' };
  }

  const fromPath = sectionFromPath(pathname);
  /*
   * A consent callback returns here. It has to land on the section it started from, or
   * the outcome banner mounts on a screen nobody is looking at. The path says which
   * section; with no path (an old bookmark), Connections.
   */
  if (params.has('gmail') || params.has('microsoft')) {
    return { tab: 'settings', settings: fromPath?.settings ?? 'connections' };
  }
  if (fromPath) return { tab: 'settings', ...fromPath };
  /*
   * `/review?view=…&source=…` is Review's own address, and an older `?state=` still reads.
   * `?tab=review` (and every alias that lands on Review) still works, and reads the same
   * two filters if it carries them.
   */
  const bare = pathname.replace(/\/+$/, '').toLowerCase();
  // Capture has its own address now that `/` is the Dashboard.
  if (bare === '/capture' && !tabParam) return LEGACY_TABS.capture!;
  const isReviewPath = bare === '/review';
  const legacy = isReviewPath ? LEGACY_TABS.review! : LEGACY_TABS[tabParam] ?? DEFAULT_LOCATION;
  if (legacy.tab === 'review') return { ...legacy, review: parseReviewFilters(search) };
  return legacy;
}

/** The address a location is shown at, so a refresh lands where the person was. */
export function pathFor(loc: Pick<Location, 'tab' | 'settings' | 'review'>): string {
  if (loc.tab === 'settings') {
    return `/settings/${ALL_SETTINGS_TABS.find((t) => t.id === loc.settings)?.slug ?? 'connections'}`;
  }
  if (loc.tab === 'review') return reviewPathFor(loc.review);
  if (loc.tab === 'capture') return '/capture';
  return '/';
}
